import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { of } from 'rxjs';
import { catchError, first, pluck, shareReplay, switchMap } from 'rxjs/operators';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { AppSettings } from '../configuration/appsettings';
import { MessageBusService, MessageBusType } from '../message-bus/message-bus.service';
import { LicenseInfo, LicenseInfoDefaults } from './license-info.interface';

/**
 * Service to aid licensing in menu and router(module load)
 */
@Injectable({
	providedIn: 'root',
	deps: [HttpClient, MessageBusService, AppSettings],
})
export class LicensingService {
	/** Observable to keep taps on loginState, uses shareReplay to buffer the current state */
	private isLoggedIn$ = this.msgBus.on<boolean>(MessageBusType.LoginState).pipe(shareReplay(1));

	/** observable to get licenced modules for current user */
	moduleData$ = this.isLoggedIn$.pipe(
		switchMap(state =>
			state && this.app.settings.licensingEnabled === true
				? this.http.get<LicenseInfo>('license').pipe(catchError(e => of(LicenseInfoDefaults())))
				: of(LicenseInfoDefaults())
		),
		pluck('modules'), // Only need to know moduleData
		shareReplay(1) // Don't repeat calls to backend when not needed.
	);

	/** Kick of listing for loginState updates make sure its executed once and buffered */
	private moduleSub = this.moduleData$.subscribe({error: console.error});

	/** Creates an instance of licencing service. */
	constructor(private http: HttpClient, private msgBus: MessageBusService, private app: AppSettings, private auth: AsamcoAuthService) {}

	/** check if the license is there. */
	async hasLicence(name: string) {
		if (name === 'noAccess') {
			// this is a way to test any route, to check for the unhappy path.
			// when you set the moduleLicenceName to 'noAccess' it will fail.
			console.warn('on purpose license fail'); //NOSONAR
			return false;
		}
		// get the module from its "buffer"
		const modules = await this.moduleData$.pipe(first()).toPromise();
		if (!Array.isArray(modules)) {
			// module is Null/Undefined, which means everything is licensed.
			// this can only happen if null/undefined back from the backend
			// lets check our token to see if licencing is off.
			const token = <any>jwtDecode(await this.auth.getPrimaryTokenAsync());
			const h = !!token.hasLicensing;
			// only return true in this case if licencing is off.
			return h === false;
		}
		return modules.find(line => line.name === name) !== undefined;
	}

	/** destroy method, for future sake. */
	destroy() {
		this.moduleSub.unsubscribe();
	}
}
